// src/components/dashboard/SearchForm.tsx
import React, { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import {DashboardSearchRequest, SelectOption, SelectOptionArrayValue} from "../api/ApiTypes.ts";
import {useConstantsApi} from "../context/ApiContext.tsx";
import {SamSelect} from "./SamSelect.tsx";

interface OrderSearchProps {
    initialValues: DashboardSearchRequest;
    onSearch: (searchParams: DashboardSearchRequest) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
}

export const OrderSearch = ({
                                initialValues,
                                onSearch,
                                onCancel,
                                isSubmitting = false}: OrderSearchProps) => {

    const [searchParams, setSearchParams] = useState<DashboardSearchRequest>(initialValues);
    const [isLoading, setLoading] = useState(true);
    const [states, setStates] = useState<SelectOption[]>([]);
    const [statuses, setStatuses] = useState<SelectOptionArrayValue[]>([]);
    const constantsApi = useConstantsApi();

    useEffect(() => {
        const loadLookupData = async () => {
            try {
                setLoading(true);

                const [statesData, statusesData] = await Promise.all([
                    constantsApi.getStates(),
                    constantsApi.getSearchStatuses()
                ]);

                setStates(statesData);
                setStatuses(statusesData);

                // Default to 'Open'
                if (!initialValues.statusRefIds) {
                    const openStatus = statusesData.find(s => s.label === 'Open');
                    if (openStatus) {
                        setSearchParams(prev => ({
                            ...prev,
                            statusRefIds: Array.isArray(openStatus.value)
                                ? openStatus.value
                                : [openStatus.value]
                        }));
                    }
                }

                // Default to 'All'
                if (initialValues.statusRefIds && initialValues.statusRefIds.length === 0) {
                    const allStatus = statusesData.find(s => s.label === 'All');
                    if (allStatus) {
                        setSearchParams(prev => ({
                            ...prev,
                            statusRefIds: Array.isArray(allStatus.value)
                                ? allStatus.value
                                : [allStatus.value]
                        }));
                    }
                }

            } catch (error) {
                console.error('Failed to load lookup data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadLookupData();
    }, []);


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle State selection from SamSelect
    const handleStateChange = (selectedOption: string) => {
        setSearchParams(prev => {
            return {
                ...prev,
                propertyState: selectedOption as string
            };
        });
    };

    // Handle status selection from SamSelect
    const handleStatusChange = (selectedOption: [] | null) => {
        setSearchParams(prev => {
            // Otherwise, set the selected status IDs
            // Assuming the value in SelectOptionArrayValue contains the array of status IDs
            return {
                ...prev,
                statusRefIds: selectedOption as number[]

            };
        });
    };

    const handleClear = () => {
        //default back to 'All' orders for order status
        const allStatus = statuses.find(s => s.label === 'All');

        setSearchParams({
            page: 1,
            limit: initialValues.limit,
            usaFileNumber: '',
            borrowerName: '',
            propertyStreet: '',
            propertyCity: '',
            propertyState: '',
            propertyZip: '',
            loanNumber: '',
            createDateFrom: '',
            createDateTo: '',
            statusRefIds: allStatus
                ? Array.isArray(allStatus.value)
                    ? allStatus.value
                    : [allStatus.value]
                : []
        });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSearch(searchParams);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* File Information */}
                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        File Number
                    </label>
                    <input
                        type="text"
                        name="usaFileNumber"
                        value={searchParams.usaFileNumber || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="File number"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Loan Number
                    </label>
                    <input
                        type="text"
                        name="loanNumber"
                        value={searchParams.loanNumber || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Loan number"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Borrower Name
                    </label>
                    <input
                        type="text"
                        name="borrowerName"
                        value={searchParams.borrowerName || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Borrower name"
                    />
                </div>

                {/* Property Address */}
                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Property Street
                    </label>
                    <input
                        type="text"
                        name="propertyStreet"
                        value={searchParams.propertyStreet || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Street address"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                    </label>
                    <input
                        type="text"
                        name="propertyCity"
                        value={searchParams.propertyCity || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="City"
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                    </label>
                    <SamSelect
                        value={searchParams.propertyState || ''}
                        onChange={handleStateChange}
                        options={states}
                        placeholder="Select a State"
                        disabled={isLoading}
                    />
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        ZIP Code
                    </label>
                    <input
                        type="text"
                        name="propertyZip"
                        value={searchParams.propertyZip || ''}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="ZIP code"
                    />
                </div>

                {/* Date Range */}
                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Create Date From
                    </label>
                    <div className="relative">
                        <input
                            type="date"
                            name="createDateFrom"
                            value={searchParams.createDateFrom || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Create Date To
                    </label>
                    <div className="relative">
                        <input
                            type="date"
                            name="createDateTo"
                            value={searchParams.createDateTo || ''}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Status Select */}
                <div className="col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                    </label>
                    <SamSelect
                        value={searchParams.statusRefIds}
                        onChange={handleStatusChange}
                        options={statuses}
                        placeholder="Loading..."
                        disabled={isLoading}
                    />
                </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={handleClear}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSubmitting}
                >
                    <span className="flex items-center gap-1">
                        <X className="h-4 w-4" />
                        Clear
                    </span>
                </button>

                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isSubmitting}
                >
                    Cancel
                </button>

                <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
                    disabled={isSubmitting}
                >
                    <Search className="h-4 w-4" />
                    {isSubmitting ? 'Searching...' : 'Search'}
                </button>
            </div>
        </form>
    );
};